import { LitElement, html, nothing, type PropertyValues } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { api, HttpError, type Circle, type FriendOffer, type FriendRequest, type FriendsList } from '../api';
import { currentLang, errorText, t } from '../i18n';
import { backGlyph, tPart } from '../bidi-html';
import { go, setLeaveGuard } from '../router';
import { subscribeLive } from '../live';
import { raidApi, type RaidOverview } from './raid/raid-api';
import { memberRaid, raidBoard } from './raid/raid-board';
import { nameOf } from './raid/raid-bits';
import { consentList, copyText, friendAskCard, linkCard, normaliseCode, shareLink, takeNewFriend, type Copied } from './friend-bits';

/**
 * Friends (beta): leads with friends. With none, **Add a friend** is the main action (a one-time link, after the
 * consent list; direct friends ticket 03) and starting or joining a circle sits below it, for a group. With
 * friends or a circle it is one list of everyone (glory, **Raid**, a mark on those in the learner's circle), then
 * the raids, then the circle's own section. Circles (v1 ticket 16): start one, join one by code, make a new code
 * (the keeper) and leave. Starting, joining and adding a friend all pass the consent screen first: what friends
 * will see, and what they never see.
 *
 * A code is shown once, when the server makes it; only its hash is kept, so
 * a lost code is replaced, never shown again. Personal mode has no circles:
 * this screen sends the learner home.
 *
 * In a circle it is also where raids start and come back (v1 ticket 47, `raid/raid-board.ts`): each friend's glory
 * and **Raid**, the armies at the gates and on their way, and the reports. A failed read of them shows the circle
 * as before.
 *
 * It stays current while it is open (`live.ts`): a friend joining or leaving, an army at the gates, a report coming
 * back all show without a reload. A refresh only replaces what the server owns (the circle, the friends and open
 * links, the raids); a step, a code or link just made, the leave question and the typed code are the learner's and
 * never move under them.
 *
 * Requests (ticket 05): **Ask by username** sits under the link in Add a friend and always ends in "Request sent"
 * (the server never says whether the name exists). A request to the learner shows on top of the screen with
 * **Accept**, which opens the same consent screen as a friend link, and **Decline**, which removes it at once and
 * says nothing.
 *
 * One **Have a code?** field (ticket 06) takes a friend's code or a circle's: a lookup says which and the right
 * consent screen opens (`submitCode`).
 *
 * A circle link (`/circle/join/<code>`, ticket 07 of direct friends) opens this screen with `code` set: the join
 * consent step comes up with the code filled in, and nothing is sent until the learner agrees. A learner already in
 * a circle sees it with the usual "already in a circle" message.
 */
type Step = 'choose' | 'consent-create' | 'consent-join' | 'friend-consent' | 'friend-make' | 'request-consent';

/** One line of the friends list: a circle member, a direct friend, or the learner. */
interface Row { id: string; name: string; you: boolean; keeper: boolean; circle: boolean; direct: boolean }

/** A friend link lives this long (the server's `OFFER_DAYS`); an open link's made date is its end minus this. */
const OFFER_DAYS = 7;
/** "It is gone already": the server's 404 for an offer, request or friend that no longer exists. */
const gone = (e: unknown) => e instanceof HttpError && e.status === 404;
/** A friend who can be ended: a direct friend, not also a circle-mate. */
const endable = (f: { direct: boolean; circle: boolean }) => f.direct && !f.circle;
const day = (iso: string | number) => new Date(iso).toLocaleDateString(currentLang(), { month: 'long', day: 'numeric' });

const CIRCLE_MAX = 10;

@customElement('circle-view')
export class CircleView extends LitElement {
  /** The code from a circle link; empty when the screen was opened plain. */
  @property() code = '';
  /** The activity entry's return path, supplied only by the validated router. */
  @property({ attribute: false }) returnTo = '';
  @state() private loading = true;
  @state() private circle: Circle | null = null;
  @state() private friends: FriendsList | null = null;
  @state() private raids?: RaidOverview;
  @state() private step: Step = 'choose';
  @state() private codeInput = '';
  /** The "Don't show this again" box on the sender's consent step, and whether that step was opened from **What friends see**. */
  @state() private seenTick = false;
  @state() private reviewing = false;
  /** A code the server just made: on screen until the learner leaves it. */
  @state() private freshCode = '';
  /** A friend link the server just made (its id and full address): on screen until the learner is done. */
  @state() private freshLink: { id: string; url: string; code: string } | null = null;
  @state() private copied: Copied = '';
  /** The link a share or copy could not put on the clipboard, shown to copy by hand until the card goes. */
  @state() private byHand = '';
  @state() private confirmLeave = false;
  @state() private busy = false;
  @state() private error = '';
  /** The "Have a code?" field is open (a typed code or an error keeps it open too). */
  @state() private codeOpen = false;
  /** A refused code, shown by the code field (and keeping it open). */
  @state() private joinError = '';
  /** The fresh link or code has been shared, copied or shown by hand: leaving the screen no longer loses it. */
  private freshSent = false;
  @state() private notice = '';
  /** Who the learner has just become friends with (from a friend link), said once. */
  @state() private newFriend = '';
  /** Whose friendship has just been ended here, said once. */
  @state() private endedName = '';
  /** The direct friend whose "End friendship" question is open under their row (one at a time). */
  @state() private confirmEnd = '';
  /** The username being typed in Ask by username; live refreshes never touch it. */
  @state() private requestInput = '';
  /** "Request sent" shows after a send, whatever was typed. */
  @state() private requestSent = false;
  /** A refusal of the learner's own request (the typed name is not a username, a cap), shown by the field. */
  @state() private requestError = '';
  /** The request whose consent screen is open (Accept was pressed); a refresh leaves it open. */
  @state() private answering: FriendRequest | null = null;
  private unsubLive?: () => void;
  /** The first read has landed: from then on a failed refresh leaves the screen as it is. */
  private loaded = false;
  /** A change came while a request of the learner's own was running: read again when it ends. */
  private stale = false;
  private readonly openedRequests = new Set<string>();

  protected createRenderRoot() { return this; }

  async connectedCallback() {
    super.connectedCallback();
    this.newFriend = takeNewFriend();
    setLeaveGuard(this.leaveGuard);
    try {
      const health = await api.health();
      if (!health.beta) {
        go('/', true);
        return;
      }
    } catch (e) {
      this.error = errorText(e);
      this.loading = false;
      return;
    }
    if (!this.isConnected) return;
    this.unsubLive = subscribeLive(() => this.load());
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.unsubLive?.();
    this.unsubLive = undefined;
    setLeaveGuard(null);
  }

  /** A link or code shown only once, not yet sent anywhere: leaving asks first (it stays under the open links, if a friend link). */
  private leaveGuard = () => ((this.freshLink || this.freshCode) && !this.freshSent && !this.byHand ? t('friends.leaveFresh') : null);

  /** After a step change or a fresh link, focus moves to the new card's heading (a card replaced under the focus drops it to the page). */
  protected updated(changed: PropertyValues) {
    const moved = (changed.has('step') && changed.get('step') !== undefined) || (changed.has('freshLink') && this.freshLink);
    if (moved && !this.loading) this.querySelector<HTMLElement>('.circle-screen h3[tabindex="-1"]:not(.friend-requests-title)')?.focus();
  }

  private get hasFriends() {
    return (this.friends?.friends.length ?? 0) > 0;
  }

  /** The circle, the friends and their raids from the server: the first read, and every refresh after a change elsewhere. */
  private async load() {
    if (this.busy) {
      this.stale = true;
      return;
    }
    try {
      // The first read of the friends must not pass for "no friends": its failure shows an error and the live check reads again.
      const [{ circle }, friends] = await Promise.all([
        api.circle(),
        api.friends().catch((e) => {
          if (!this.loaded) throw e;
          return this.friends;
        }),
      ]);
      if (this.busy) {
        this.stale = true;
        return;
      }
      const first = !this.loaded;
      this.friends = friends;
      // A friend who is gone (or now also in the circle) meanwhile has nothing left to end: close that question quietly.
      if (this.confirmEnd && !friends?.friends.some((f) => f.id === this.confirmEnd && endable(f))) this.confirmEnd = '';
      if (circle) this.circle = circle;
      else {
        // Not in a circle (or taken out of it on another device): nothing of one stays on screen.
        if (this.circle) this.freshCode = '';
        this.circle = null;
      }
      if (circle || this.hasFriends) this.raids = await raidApi.overview().catch(() => this.raids);
      else this.raids = undefined;
      if (first) {
        this.error = '';
        if (this.code) {
          if (circle) this.error = t('error.already-in-circle');
          else {
            this.codeInput = this.code;
            this.step = 'consent-join';
          }
        }
      }
      this.loaded = true;
    } catch (e) {
      if (!this.loaded) this.error = errorText(e);
      throw e; // the next check reads again
    } finally {
      this.loading = false;
      if (this.friends?.incoming.length) {
        await this.updateComplete;
        await this.markVisibleRequestsOpened();
      }
    }
  }

  /** A background friends read is not a view. Mark requests seen only after their rows render on this screen. */
  private async markVisibleRequestsOpened() {
    const incoming = this.friends?.incoming ?? [];
    const pending = incoming.filter((request) => !this.openedRequests.has(request.id));
    if (!pending.length) return;
    try {
      for (const request of pending) {
        await api.activityRequestOpened(request.id);
        this.openedRequests.add(request.id);
      }
    } catch { /* the request stays available; a later screen or refresh can mark it seen */ }
  }

  private async readRaids() {
    this.raids = await raidApi.overview().catch(() => undefined);
  }

  /** A new code or link starts unshared, uncopied, with nothing to copy by hand. */
  private resetShare() {
    this.freshSent = false;
    this.copied = '';
    this.byHand = '';
  }

  private clearMessages() {
    this.error = '';
    this.notice = '';
    this.newFriend = '';
    this.endedName = '';
  }

  /** One request at a time; a refusal shows its own words (`error.<code>`). */
  private async run(fn: () => Promise<void>, onFail?: () => void) {
    if (this.busy) return;
    this.busy = true;
    this.clearMessages();
    this.joinError = '';
    this.requestError = '';
    this.requestSent = false;
    try {
      await fn();
    } catch (e) {
      this.error = errorText(e);
      onFail?.();
    } finally {
      this.busy = false;
      if (this.stale) {
        this.stale = false;
        void this.load().catch(() => {});
      }
    }
  }

  private toConsent(step: Step) {
    this.step = step;
    this.clearMessages();
  }

  /** Add a friend: the consent list first, unless the learner chose not to see it again. */
  private startAdd = () => {
    this.reviewing = false;
    this.seenTick = !!this.friends?.consentSeen;
    this.toConsent(this.seenTick ? 'friend-make' : 'friend-consent');
  };

  /** **What friends see**: the list again from the add step, with the stored choice in the box. */
  private reviewList = () => {
    this.reviewing = true;
    this.seenTick = !!this.friends?.consentSeen;
    this.toConsent('friend-consent');
  };

  /** Agree on the sender's consent step: a changed box is stored first; the choice only hides the screen. */
  private agreeAdd = () =>
    this.run(async () => {
      if (this.seenTick !== !!this.friends?.consentSeen) {
        const r = await api.setFriendConsentSeen(this.seenTick);
        if (this.friends) this.friends = { ...this.friends, consentSeen: r.consentSeen };
      }
      this.reviewing = false;
      this.toConsent('friend-make');
    });

  private create = () =>
    this.run(async () => {
      const r = await api.createCircle();
      this.circle = r.circle;
      this.freshCode = r.code;
      this.resetShare();
      this.step = 'choose';
      void this.readRaids();
    }, () => (this.step = 'choose'));

  private join = () =>
    this.run(async () => {
      const r = await api.joinCircle(this.codeInput.trim());
      this.circle = r.circle;
      this.codeInput = '';
      this.step = 'choose';
      void this.readRaids();
      if (this.code) go('/circle', true); // Back must not return to the link
    }, () => {
      this.step = 'choose';
      this.joinError = this.error;
      this.error = '';
    });

  /**
   * One field for a friend's code and a circle's code: one lookup says which. A friend's opens the same naming
   * consent screen as the link (the secret goes in the path, as the link's does); a circle's opens the join consent
   * with the code filled in. Nothing is made here; a refusal shows by the field.
   */
  private submitCode = () => {
    const typed = this.codeInput.trim();
    if (!typed) return;
    void this.run(async () => {
      let r;
      try {
        r = await api.lookupFriendCode(typed);
      } catch (e) {
        // A typed code is not a link: the refusal says "code", as the circle's own does.
        if (e instanceof HttpError && e.code === 'friend-offer-invalid') {
          this.joinError = t('error.circle-code-invalid');
          return;
        }
        if (e instanceof HttpError && e.code === 'friend-own-link') {
          this.joinError = t('friends.ownCode');
          return;
        }
        throw e;
      }
      if (r.kind === 'friend') {
        go(`/friend/${normaliseCode(typed)}`);
        return;
      }
      // Already in a circle: the join route's own refusal, shown without a screen that cannot lead anywhere.
      if (this.circle) this.joinError = t('error.already-in-circle');
      else this.step = 'consent-join';
    }, () => {
      this.joinError = this.error;
      this.error = '';
    });
  };

  private newCode = () =>
    this.run(async () => {
      const r = await api.newCircleCode();
      this.circle = r.circle;
      this.freshCode = r.code;
      this.resetShare();
    });

  private leave = () =>
    this.run(async () => {
      await api.leaveCircle();
      this.circle = null;
      this.raids = undefined;
      this.freshCode = '';
      this.confirmLeave = false;
      this.stale = true; // leaving ends circle-only friendships: read the friends again
      this.notice = t('circle.left');
    });

  /** End a direct friendship at once; a 404 means they were already gone. The list is read again either way. */
  private endFriend = (id: string, name: string) =>
    this.run(async () => {
      try {
        await api.endFriendship(id);
        this.endedName = name;
      } catch (e) {
        if (!gone(e)) throw e;
      }
      this.confirmEnd = '';
      if (this.friends) this.friends = { ...this.friends, friends: this.friends.friends.filter((f) => f.id !== id) };
      this.stale = true; // read the friends and their raids again
    });

  /** Make a friend link after the consent list; it is shown this once. */
  private makeLink = () =>
    this.run(async () => {
      const l = await api.makeFriendLink();
      this.freshLink = { id: l.id, url: `${location.origin}${l.path}`, code: l.code };
      this.resetShare();
      if (this.friends) this.friends = { ...this.friends, offers: [...this.friends.offers, { id: l.id, kind: 'link', expiresAt: l.expiresAt }] };
    });

  private withdraw = (id: string) =>
    this.run(async () => {
      const kind = this.friends?.offers.find((o) => o.id === id)?.kind;
      try {
        await api.withdrawFriendOffer(id);
      } catch (e) {
        if (!gone(e)) throw e; // 404: it is gone already
      }
      if (this.friends) this.friends = { ...this.friends, offers: this.friends.offers.filter((o) => o.id !== id) };
      if (this.freshLink?.id === id) {
        // The next link starts from the consent list again, as every link does, unless the learner chose not to see it.
        this.freshLink = null;
        this.startAdd();
      }
      this.notice = t(kind === 'request' ? 'friends.requestWithdrawn' : 'friends.withdrawn');
    });

  /** Ask a username to be friends: "Request sent" whatever was typed; a crossed request makes friends at once. */
  private sendRequest = () => {
    const name = this.requestInput.trim();
    if (!name) return;
    void this.run(async () => {
      const r = await api.sendFriendRequest(name);
      this.requestInput = '';
      if (r.friend) this.newFriend = r.friend.name;
      else this.requestSent = true;
      this.stale = true; // read the open requests (or the new friend) again
    }, () => {
      this.requestError = this.error;
      this.error = '';
    });
  };

  /** Accept opens the consent screen naming the sender; nothing is made until the learner agrees there. */
  private openAnswer(r: FriendRequest) {
    this.answering = r;
    this.toConsent('request-consent');
  }

  private closeAnswer = () => {
    this.answering = null;
    this.step = 'choose';
    this.error = '';
  };

  private agreeRequest = () => {
    const req = this.answering;
    if (!req) return;
    void this.run(async () => {
      try {
        const r = await api.acceptFriendRequest(req.id);
        if (!r.already) this.newFriend = r.friend.name;
      } catch (e) {
        // Gone meanwhile (withdrawn or expired): back to the list, with the error line. A full side stays on the screen.
        if (gone(e)) this.closeAnswer();
        throw e;
      }
      this.answering = null;
      this.step = 'choose';
      this.dropIncoming(req.id);
      this.stale = true;
    });
  };

  private dropIncoming(id: string) {
    if (this.friends) this.friends = { ...this.friends, incoming: this.friends.incoming.filter((r) => r.id !== id) };
  }

  /** Decline at once, with no question and no message; the sender is never told. */
  private decline = (r: FriendRequest) =>
    this.run(async () => {
      this.dropIncoming(r.id);
      try {
        await api.declineFriendRequest(r.id);
      } catch (e) {
        if (!gone(e)) {
          this.stale = true; // it may still be there: read again
          throw e;
        }
      }
      // The row the focus was on is gone: move it to the requests that are left, else the screen's first heading.
      void this.updateComplete.then(() =>
        (this.querySelector<HTMLElement>('.friend-requests-title') ?? this.querySelector<HTMLElement>('.circle-screen h3[tabindex="-1"]'))?.focus());
    });

  private doneAdding = () => {
    this.step = 'choose';
    this.freshLink = null;
    this.resetShare();
    this.error = '';
    this.notice = '';
    this.requestSent = false;
    this.requestError = '';
  };

  private copy = async (text: string, what: 'link' | 'code', link = text) => {
    if (await copyText(text)) {
      this.copied = what;
      this.byHand = '';
      this.freshSent = true;
    } else this.byHand = link;
  };

  /** The share sheet when there is one, else the link is copied (and the button says so). */
  private share = async (url: string) => {
    const r = await shareLink(url);
    if (r !== 'none' && r !== 'failed') this.freshSent = true;
    if (r === 'copied') this.copied = 'share';
    if (r === 'failed') this.byHand = url;
  };

  private circleLink = () => `${location.origin}/circle/join/${encodeURIComponent(this.freshCode)}`;

  /** The screen's one card: loading, the friend ask, adding, the circle consent, the friends, or the first choice. */
  private body(adding: boolean, circleStep: boolean) {
    if (this.loading) return html`<p class="muted">${t('circle.loading')}</p>`;
    if (!this.loaded) return nothing;
    if (this.step === 'request-consent' && this.answering) {
      return friendAskCard({ name: this.answering.name, busy: this.busy, error: this.error, onAgree: this.agreeRequest, onCancel: this.closeAnswer });
    }
    if (adding) return this.renderAdd();
    if (circleStep && !this.circle) return this.renderConsent();
    return this.circle || this.hasFriends ? this.renderFriends() : this.renderChoose();
  }

  render() {
    const adding = this.step === 'friend-consent' || this.step === 'friend-make';
    const circleStep = this.step === 'consent-create' || this.step === 'consent-join';
    return html`
      <div class="screen circle-screen">
        <div class="topbar">
          <button class="btn ghost icon" @click=${() => go(this.returnTo || '/')} aria-label=${this.returnTo ? t('activity.return') : t('common.homeAria')}>${backGlyph()}</button>
          <h1 style="font-size:17px">${t('circle.title')}</h1>
        </div>
        ${this.body(adding, circleStep)}
        ${this.error && !(adding && this.step === 'friend-make') && this.step !== 'request-consent' ? this.errorLine() : nothing}
      </div>
    `;
  }

  private errorLine() {
    return html`<p class="circle-error" role="alert">${this.error}</p>`;
  }

  private notices() {
    return html`${this.notice ? html`<p class="muted circle-notice" role="status">${this.notice}</p>` : nothing}
      ${this.newFriend
        ? html`<p class="muted circle-notice friend-notice" role="status">${tPart('friends.nowFriends', 'name', nameOf(this.newFriend))}</p>`
        : nothing}
      ${this.endedName
        ? html`<p class="muted circle-notice friend-notice" role="status">${tPart('friends.ended', 'name', nameOf(this.endedName))}</p>`
        : nothing}`;
  }

  /** Starting a circle: the option for a group (joining one is the "Have a code?" field). */
  private renderGroup(titled = true) {
    return html`
      <div class="card">
        ${titled ? html`<h3>${t('friends.groupTitle')}</h3>` : nothing}
        <p class="muted">${t('circle.intro', { max: CIRCLE_MAX })}</p>
        <button class="btn block circle-start" @click=${() => this.toConsent('consent-create')}>${t('circle.start')}</button>
      </div>
    `;
  }

  /** One quiet "Have a code?" field for everyone: a friend's code or a circle's. It stays open while a code is typed or an error shows. */
  private renderCodeField() {
    return html`<details class="friend-code" ?open=${this.codeOpen || !!this.codeInput || !!this.joinError} @toggle=${(e: Event) => (this.codeOpen = (e.target as HTMLDetailsElement).open)}>
      <summary class="muted">${t('circle.haveCode')}</summary>
      <form class="card friend-code-form" @submit=${(e: Event) => { e.preventDefault(); this.submitCode(); }}>
        <label class="muted small" for="code-field">${t('circle.codeLabel')}</label>
        <input
          id="code-field"
          name="code-field"
          class="circle-code-input"
          dir="ltr"
          autocomplete="off"
          autocapitalize="characters"
          spellcheck="false"
          placeholder="ABCDE-FGHJK-MNPQR-STVWX"
          aria-invalid=${this.joinError ? 'true' : 'false'}
          aria-describedby=${this.joinError ? 'code-field-error' : nothing}
          .value=${this.codeInput}
          @input=${(e: Event) => { this.codeInput = (e.target as HTMLInputElement).value; this.joinError = ''; }}
        />
        ${this.joinError ? html`<p id="code-field-error" class="circle-error circle-join-error" role="alert">${this.joinError}</p>` : nothing}
        <button type="submit" class="btn block code-go" ?disabled=${this.busy || !this.codeInput.trim()}>${t('circle.join')}</button>
      </form>
    </details>`;
  }

  /** No friends and no circle: one main action, and the circle below it for a group. */
  private renderChoose() {
    return html`
      ${this.notices()}
      ${this.renderIncoming()}
      <div class="card friend-first">
        <h3 tabindex="-1">${t('circle.introTitle')}</h3>
        <p class="muted">${t('friends.addHint')}</p>
        <button class="btn primary block friend-add" @click=${this.startAdd}>${t('friends.add')}</button>
      </div>
      <div class="card friend-preview">
        <h3>${t('friends.previewTitle')}</h3>
        <p class="muted">${t('friends.previewHint')}</p>
        <button class="btn block friend-preview-go" @click=${() => go('/raid/tour?context=preview')}>${t('friends.previewTitle')}</button>
      </div>
      ${this.renderCodeField()}
      ${this.renderOffers(this.friends?.offers ?? [])}
      ${this.renderGroup()}
    `;
  }

  /** What friends will see and never see, before a circle is started or joined. */
  private renderConsent() {
    const joining = this.step === 'consent-join';
    return html`
      <div class="card circle-consent">
        <h3 tabindex="-1">${joining ? t('circle.consentJoin') : t('circle.consentStart')}</h3>
        ${consentList()}
        <p class="muted small">${t('circle.leaveAnyTime')}</p>
        <button class="btn primary block circle-agree" ?disabled=${this.busy} @click=${joining ? this.join : this.create}>
          ${joining ? t('circle.agreeJoin') : t('circle.agreeStart')}
        </button>
        <button class="btn ghost block circle-cancel" style="margin-top:8px" ?disabled=${this.busy} @click=${() => (this.step = 'choose')}>
          ${t('circle.cancel')}
        </button>
      </div>
    `;
  }

  /** Add a friend: the consent list first, then Make a link, the fresh link once, and the open links with Withdraw. */
  private renderAdd() {
    if (this.step === 'friend-consent') {
      return html`
        <div class="card friend-consent">
          <h3 tabindex="-1">${t('friends.consentTitle')}</h3>
          ${consentList()}
          <p class="muted small friend-end-any">${t('friends.endAnyTime')}</p>
          <label class="friend-consent-seen">
            <input type="checkbox" .checked=${this.seenTick} @change=${(e: Event) => { this.seenTick = (e.target as HTMLInputElement).checked; }} />
            <span>${t('friends.dontShow')}</span>
          </label>
          <button class="btn primary block friend-agree" ?disabled=${this.busy} @click=${this.agreeAdd}>${t('friends.agree')}</button>
          <button class="btn ghost block friend-cancel" style="margin-top:8px" ?disabled=${this.busy}
            @click=${() => (this.reviewing ? this.toConsent('friend-make') : this.doneAdding())}>${t('circle.cancel')}</button>
        </div>
      `;
    }
    const offers = this.friends?.offers ?? [];
    const fresh = this.freshLink;
    const username = this.friends?.username;
    return html`
      ${this.notices()}
      ${username ? html`<p class="muted small friend-yourname">${tPart('friends.yourName', 'name', nameOf(username))}</p>` : nothing}
      ${fresh
        ? linkCard({
            linkFirst: true,
            title: t('friends.linkTitle'),
            hint: `${t('friends.linkHint', { days: OFFER_DAYS })} ${t('friends.linkCodeHint')}`,
            code: fresh.code,
            copied: this.copied,
            byHand: this.byHand,
            onShare: () => this.share(fresh.url),
            onCopyLink: () => this.copy(fresh.url, 'link'),
            onCopyCode: () => this.copy(fresh.code, 'code', fresh.url),
            note: offers.some((o) => o.id === fresh.id) ? undefined : t('friends.linkGone'),
          })
        : html`<div class="card friend-make">
            <h3 tabindex="-1">${t('friends.addTitle')}</h3>
            <p class="muted">${t('friends.makeHint', { days: OFFER_DAYS })}</p>
            <button class="btn primary block friend-make-link" ?disabled=${this.busy} @click=${this.makeLink}>${t('friends.make')}</button>
            ${this.error ? this.errorLine() : nothing}
          </div>`}
      ${fresh && this.error ? this.errorLine() : nothing}
      ${this.renderAsk()}
      ${this.renderOffers(offers)}
      <button class="btn ghost block friend-what" @click=${this.reviewList}>${t('friends.whatSee')}</button>
      <button class="btn ghost block friend-done" @click=${this.doneAdding}>${t('friends.done')}</button>
    `;
  }

  /** Ask by username: one plain field, no suggestions; the answer is "Request sent" whatever was typed. */
  private renderAsk() {
    return html`<form class="card friend-ask-name" @submit=${(e: Event) => { e.preventDefault(); this.sendRequest(); }}>
      <h3>${t('friends.askTitle')}</h3>
      <p class="muted small">${t('friends.askHint')}</p>
      <label class="muted small" for="friend-username">${t('friends.askLabel')}</label>
      <input id="friend-username" name="friend-username" class="circle-code-input request-name" dir="auto" autocomplete="off" autocapitalize="off"
        autocorrect="off" spellcheck="false" aria-invalid=${this.requestError ? 'true' : 'false'} aria-describedby=${this.requestError ? 'friend-username-error' : nothing}
        .value=${this.requestInput} @input=${(e: Event) => { this.requestInput = (e.target as HTMLInputElement).value; this.requestSent = false; this.requestError = ''; }} />
      ${this.requestError ? html`<p id="friend-username-error" class="circle-error request-error" role="alert">${this.requestError}</p>` : nothing}
      <button type="submit" class="btn block request-send" ?disabled=${this.busy || !this.requestInput.trim()}>${t('friends.send')}</button>
      <p class="muted small request-status" role="status">${this.requestSent ? t('friends.requestSent') : ''}</p>
    </form>`;
  }

  /** Requests to the learner, on top of Friends: **Accept** opens the consent screen, **Decline** removes the row at once. */
  private renderIncoming() {
    const incoming = this.friends?.incoming ?? [];
    if (!incoming.length) return nothing;
    return html`<div class="card friend-requests">
      <h3 class="friend-requests-title" tabindex="-1">${t('friends.requestsTitle')}</h3>
      <ul class="rb-list">
        ${incoming.map(
          (r) => html`<li class="rb-row friend-request">
            <span class="rb-text">${tPart('friends.wants', 'name', nameOf(r.name))}</span>
            <button class="btn primary request-accept" ?disabled=${this.busy} aria-label=${t('friends.acceptLabel', { name: r.name })} @click=${() => this.openAnswer(r)}>${t('friends.accept')}</button>
            <button class="btn request-decline" ?disabled=${this.busy} aria-label=${t('friends.declineLabel', { name: r.name })} @click=${() => this.decline(r)}>${t('friends.decline')}</button>
          </li>`,
        )}
      </ul>
    </div>`;
  }

  private renderOffers(offers: FriendOffer[]) {
    if (!offers.length) return nothing;
    const made = (o: FriendOffer) => day(new Date(o.expiresAt).getTime() - OFFER_DAYS * 86_400_000);
    return html`<div class="card friend-offers">
      <h3>${offers.some((o) => o.kind === 'request') ? t('friends.openTitleBoth') : t('friends.openTitle')}</h3>
      <ul class="rb-list">
        ${offers.map((o) => {
          const name = nameOf(o.name ?? '');
          return html`<li class="rb-row friend-offer">
            <span class="rb-text muted small">${o.kind === 'request'
              ? tPart('friends.requestLine', 'name', name, { until: day(o.expiresAt) })
              : t('friends.openLine', { made: made(o), until: day(o.expiresAt) })}</span>
            <button class="btn friend-withdraw" ?disabled=${this.busy} aria-label=${o.kind === 'request' ? t('friends.withdrawRequestLabel', { name: o.name ?? '' }) : t('friends.withdrawLabel', { made: made(o) })} @click=${() => this.withdraw(o.id)}>${t('friends.withdraw')}</button>
          </li>`;
        })}
      </ul>
    </div>`;
  }

  /** Everyone the learner is friends with, once: circle members in the circle's order, then direct friends. */
  private rows(): Row[] {
    const rows: Row[] = [];
    if (this.circle) for (const m of this.circle.members) rows.push({ id: m.id, name: m.name, you: m.you, keeper: m.keeper, circle: true, direct: false });
    else if (this.friends && this.raids) rows.push({ id: '', name: this.friends.username, you: true, keeper: false, circle: false, direct: false });
    for (const f of this.friends?.friends ?? []) {
      if (!rows.some((r) => r.id === f.id)) rows.push({ id: f.id, name: f.name, you: false, keeper: false, circle: f.circle, direct: f.direct });
    }
    return rows;
  }

  private renderFriends() {
    const c = this.circle;
    const rows = this.rows();
    // Direct friends outside the circle make the list mixed: then a mark tells the circle's apart.
    const mixed = rows.some((r) => !r.circle && !r.you);
    // A list with a `⋯` anywhere keeps its slot on every row, so glory and Raid line up (the end of this row's first line).
    const hasMore = rows.some((r) => endable(r) && !r.you);
    const keeper = c?.members.find((m) => m.keeper);
    return html`
      ${this.notices()}
      ${this.renderIncoming()}
      <div class="card circle-members friend-list">
        <h3 tabindex="-1">${c && !mixed ? t('circle.count', { n: c.members.length, max: c.max }) : t('friends.listTitle')}</h3>
        <ul class="circle-member-list ${hasMore ? 'has-more' : ''}">
          ${rows.map(
            (m) => html`<li class="circle-member ${m.you ? 'is-you' : ''}">
              <span class="circle-avatar" data-i=${[...m.name.trim()][0]?.toLocaleUpperCase() ?? ''} aria-hidden="true"></span>
              <span class="circle-who">${nameOf(m.name)}
              ${m.keeper ? html`<span class="pill">${t('circle.keeper')}</span>` : nothing}
              ${mixed && m.circle && !m.you ? html`<span class="pill friend-mark"><span aria-hidden="true">${t('friends.circleMark')}</span><span class="sr-only">${t('friends.circleMarkLabel')}</span></span>` : nothing}
              ${m.you ? html`<span class="pill accent">${t('circle.you')}</span>` : nothing}</span>
              ${memberRaid(this.raids, m, endable(m) && !m.you
                ? html`<button class="btn ghost icon friend-more" aria-label=${t('friends.moreFor', { name: m.name })} aria-expanded=${this.confirmEnd === m.id ? 'true' : 'false'}
                    data-id=${m.id} @click=${() => (this.confirmEnd === m.id ? this.closeEnd(m.id) : this.openEnd(m.id))}>⋯</button>`
                : hasMore ? html`<span class="friend-more-slot" aria-hidden="true"></span>` : nothing)}
              ${m.id && this.confirmEnd === m.id ? this.renderEnd(m) : nothing}
            </li>`,
          )}
        </ul>
        ${this.raids && this.raids.launchesLeft <= 0 && this.raids.friends.length ? html`<p class="muted small rb-limit">${t('raid.block.limit')}</p>` : nothing}
        ${this.raids ? html`<p class="rb-how"><button type="button" class="rb-tour" @click=${() => go('/raid/tour')}>${t('raid.tour.title')}</button></p>` : nothing}
      </div>
      <button class="btn block friend-add" @click=${this.startAdd}>${t('friends.add')}</button>
      ${this.renderCodeField()}
      ${this.renderOffers(this.friends?.offers ?? [])}
      ${raidBoard(this.raids)}
      ${c ? this.renderCircleSection(c, mixed, keeper?.name) : html`<details class="friend-group"><summary class="muted">${t('friends.groupTitle')}</summary>${this.renderGroup(false)}</details>`}
    `;
  }

  /** Open the question: focus moves into it. */
  private openEnd(id: string) {
    this.confirmEnd = id;
    void this.updateComplete.then(() => this.querySelector<HTMLElement>('.friend-end-panel')?.focus());
  }

  /** Close it (Keep, or the button again): focus goes back to the row's button. */
  private closeEnd(id: string) {
    this.confirmEnd = '';
    void this.updateComplete.then(() => [...this.querySelectorAll<HTMLElement>('.friend-more')].find((b) => b.dataset.id === id)?.focus());
  }

  /** The question under a direct friend's row: what ending does, then End friendship or Keep. */
  private renderEnd(m: Row) {
    return html`<div class="friend-end-panel" tabindex="-1" role="group" aria-label=${t('friends.moreFor', { name: m.name })}>
      <p>${tPart('friends.endConfirm', 'name', nameOf(m.name))}</p>
      <button class="btn danger friend-end" ?disabled=${this.busy} @click=${() => this.endFriend(m.id, m.name)}>${t('friends.end')}</button>
      <button class="btn ghost friend-keep" ?disabled=${this.busy} @click=${() => this.closeEnd(m.id)}>${t('friends.keep')}</button>
    </div>`;
  }

  /** The circle's own part: its code just made, the keeper's tools, leaving. */
  private renderCircleSection(c: Circle, mixed: boolean, keeperName: string | undefined) {
    return html`
      ${mixed ? html`<p class="circle-count"><strong>${t('friends.circleTitle')}</strong>${t('common.dot')}${t('circle.count', { n: c.members.length, max: c.max })}</p>` : nothing}
      ${this.freshCode
        ? linkCard({
            title: t('circle.codeTitle'),
            hint: t('circle.codeHint'),
            code: this.freshCode,
            copied: this.copied,
            byHand: this.byHand,
            onShare: () => this.share(this.circleLink()),
            onCopyLink: () => this.copy(this.circleLink(), 'link'),
            onCopyCode: () => this.copy(this.freshCode, 'code', this.circleLink()),
          })
        : nothing}
      ${c.keeper
        ? html`<div class="card">
            ${c.hasCode ? nothing : html`<p class="muted small circle-no-code">${t('circle.noCode')}</p>`}
            <button class="btn block circle-new-code" ?disabled=${this.busy} @click=${this.newCode}>${t('circle.newCode')}</button>
            ${c.hasCode ? html`<p class="muted small">${t('circle.newCodeHint')}</p>` : nothing}
          </div>`
        : keeperName
          ? html`<p class="muted small circle-ask">${t('circle.askKeeper', { name: keeperName })}</p>`
          : nothing}
      ${this.confirmLeave
        ? html`<div class="card circle-leave-confirm" role="alertdialog" aria-labelledby="circle-leave-q">
            <p id="circle-leave-q">${this.friends?.friends.some((f) => f.circle && f.direct) ? t('circle.leaveConfirmKeep') : t('circle.leaveConfirm')}</p>
            <button class="btn danger block circle-leave-yes" ?disabled=${this.busy} @click=${this.leave}>${t('circle.leaveYes')}</button>
            <button class="btn ghost block circle-leave-no" style="margin-top:8px" ?disabled=${this.busy} @click=${() => (this.confirmLeave = false)}>
              ${t('circle.leaveNo')}
            </button>
          </div>`
        : html`<button class="btn ghost danger block circle-leave" @click=${() => (this.confirmLeave = true)}>${t('circle.leave')}</button>`}
    `;
  }
}
